import { Scroll } from './Scroll';
import { Maths } from '../utils/Maths';
import { Functions } from '../utils/Functions';
import { Metrics } from '../core/Metrics';

export default class Scroll_Item {
  item;
  id;
  index;
  top = 0;
  width = 0;
  height = 0;
  progress = 0;

  onShow = null;
  onHide = null;
  onMove = null;

  isShow = false;
  firstShow = true;

  _limits = {
    default: { start: 0, end: 0 },
    inside: { start: 0, end: 0 },
    top: { start: 0, end: 0 },
    toTop: { start: 0, end: 0 },
    bottom: { start: 0, end: 0 },
  };

  constructor(item, index) {
    this.item = item;
    this.index = index;
    this.id = Functions.getId(item);

    if (Scroll.isDebug) this.addDebugInfo();
  }

  // Getters — solo aritmética; sin layout reads en el hot path
  get y() {
    return Scroll.y - this.top;
  }

  get progressItem() {
    return Maths.normalize(this._limits.default.end, this._limits.default.start, this.y);
  }

  // Recorregut SENCER de l'element pel viewport, clampat a [0,1]:
  //   0 = el TOP de l'element entra pel BOTTOM del viewport
  //   1 = el BOTTOM de l'element surt pel TOP del viewport
  // MATEIX resultat que la solució temporal (vh − rect.top) / (vh + rect.height), però PRECALCULAT al
  // resize: el getter NO fa getBoundingClientRect() (cap lectura de layout per frame), només aritmètica
  // amb this.y (que ja és Scroll.y − this.top, i this.top es captura al resize). rect.top = −this.y.
  // _piVh i _piRange es calculen a resizeLimits() amb el viewport REAL (window.innerHeight), no
  // Metrics.HEIGHT, que es pot quedar desfasat al simulador/inspector.
  get progressInside() {
    if (!this._piRange) return 0;
    return Maths.clamp((this.y + this._piVh) / this._piRange, 0, 1);
  }

  get progressTop() {
    return Maths.normalize(this._limits.top.end, this._limits.top.start, this.y);
  }

  // 0 = top al borde inferior; 1 = top en la zona superior
  get progressToTop() {
    return Maths.normalize(this._limits.toTop.end, this._limits.toTop.start, this.y);
  }

  get progressBottom() {
    return Maths.normalize(this._limits.bottom.end, this._limits.bottom.start, this.y);
  }

  // Scroll updates
  update() {
    if (!this.isShow) return;

    this.progress = this.progressItem;
    this.draw();
    this.drawHook();
    this.loop();

    if (Scroll.isDebug) {
      this.updateDebugInfo(
        this.progress,
        this.progressInside,
        this.progressTop,
        this.progressBottom
      );
    }
  }

  drawHook() { }
  draw() {
    this.item.style.setProperty('--y', this.y);
    this.item.style.setProperty('--progress', `${this.progress}`);
    this.item.style.setProperty('--progress-inside', `${this.progressInside}`);
    this.item.style.setProperty('--progress-top', `${this.progressTop}`);
    this.item.style.setProperty('--progress-bottom', `${this.progressBottom}`);
    this.item.style.setProperty('--progress-to-top', `${this.progressToTop}`);

    if (this.onMove) {
      this.onMove({ x: 0, y: this.y, z: 0 }, { width: this.width, height: this.height });
    }
  }

  loop() { }

  // Una sola lectura de layout (no en cada frame)
  _syncTop() {
    this.top = this.item.getBoundingClientRect().top + (Scroll.y ?? 0);
  }

  show() {
    if (this.isShow) return;

    this.onShow?.();
    if (!this.onHide) this.onShow = null;

    this.firstShow = false;
    this.isShow = true;
  }

  hide() {
    if (!this.isShow) return;

    this.onHide?.();
    this.isShow = false;
  }

  resize() {
    this.width = this.item.offsetWidth;
    this.height = this.item.offsetHeight;
  }

  resizeLimits(viewportW, viewportH) {
    this._syncTop();

    this._limits = {
      default: { start: -viewportH, end: this.height },
      inside: { start: 0, end: this.height - viewportH },
      top: { start: 0, end: this.height },
      toTop: { start: -viewportH, end: 0 },
      bottom: { start: -viewportH, end: this.height - viewportH },
    };

    // progressInside (recorregut sencer) PRECALCULAT: viewport REAL (window.innerHeight, no viewportH/Metrics
    // que es pot desfasar al simulador). El getter només farà (this.y + _piVh) / _piRange.
    this._piVh = window.innerHeight;
    this._piRange = this.height + this._piVh;

    this.item.style.setProperty('--scroll-start', `${this._limits.default.start}`);
    this.item.style.setProperty('--scroll-end', `${this._limits.default.end}`);
    this.item.style.setProperty('--scroll-inside-start', `${this._limits.inside.start}`);
    this.item.style.setProperty('--scroll-inside-end', `${this._limits.inside.end}`);
  }

  dispose() {
    // Si l'item encara estava visible quan es destrueix la pàgina (p.ex. sense haver-lo
    // fet scroll fora abans de navegar), cal disparar onHide igualment — sinó efectes
    // secundaris globals (com Header.setWhite) queden encallats per sempre.
    if (this.isShow) this.hide();
    this.item = null;
  }

  // Debug
  addDebugInfo() {
    const panel = document.createElement('div');
    panel.className = 'debugInfo';
    panel.innerHTML = `
      <div class="info">
        <div class="title-bar">Scroll item info</div>
        <ul>
          <li class="__position"><span>Position:</span> 0</li>
          <li class="__progress"><span>Progress:</span> 0</li>
          <li class="__progressInside"><span>P Inside:</span> 0</li>
          <li class="__progressTop0"><span>Pr Top:</span> 0</li>
          <li class="__progressBottom0"><span>Pr Bot:</span> 0</li>
        </ul>
      </div>
    `;
    this.item.appendChild(panel);
  }

  updateDebugInfo(progress, inside, top, bottom) {
    const panel = this.item.querySelector('.debugInfo');
    if (!panel) return;

    const setText = (selector, value) => {
      const el = panel.querySelector(selector);
      if (el) el.innerHTML = `<span>${el.querySelector('span')?.textContent}</span> ${value}`;
    };

    setText('.__position', this.y.toFixed(2));
    setText('.__progress', progress.toFixed(2));
    setText('.__progressInside', inside.toFixed(2));
    setText('.__progressTop0', top.toFixed(2));
    setText('.__progressBottom0', bottom.toFixed(2));
  }
}
